// src/utils/callAvailability.js
//
// Phase N9B: single shared decision for "can I call this profile right
// now" - reused by HomePage, OnlineUsers, and UserProfilePage so the three
// call buttons can never silently drift apart. inCall is the server-
// authoritative Busy mirror (see functions/calls/locks.js); a Busy user
// stays fully visible/rendered wherever they already appeared, only the
// call action itself is disabled.
//
// Pure, no React/Firebase imports, so it is directly unit-testable without
// hitting this project's known react-router-dom module-resolution
// limitation for component render tests.

/**
 * @param {{ inCall?: boolean, receiveMode?: string, settings?: { messages?: { receiveMode?: string } } }} user
 * @return {boolean}
 */
export function isUserBusy(user) {
  return user?.inCall === true;
}

/**
 * @param {{ inCall?: boolean, receiveMode?: string, settings?: { messages?: { receiveMode?: string } } }} user
 * @return {boolean}
 */
export function canCallUser(user) {
  const mode = String(
    user?.receiveMode ?? user?.settings?.messages?.receiveMode ?? "both"
  )
    .trim()
    .toLowerCase();
  return mode === "both" && !isUserBusy(user);
}

/**
 * @param {{ name?: string, displayName?: string, username?: string }} user
 * @param {Function} [t]
 * @return {string}
 */
export function resolveDisplayName(user, t) {
  const name = String(
    user?.name || user?.displayName || user?.username || ""
  ).trim();
  if (name) return name;
  return t ? t("user", { defaultValue: "User" }) : "User";
}

/**
 * Phase N9D: single shared decision for "what should tapping this user's
 * call action do right now" - reused by HomePage, OnlineUsers, and
 * UserProfilePage so a Busy tap can never silently start a call, and the
 * name-aware Busy message can never drift between pages. A Busy user's
 * button must stay tappable (no HTML `disabled`) so this can fire and
 * give feedback instead of doing nothing.
 *
 * @param {{ inCall?: boolean, receiveMode?: string, name?: string, displayName?: string, username?: string, settings?: { messages?: { receiveMode?: string } } }} user
 * @param {Function} [t] i18next translate function; when omitted, falls
 *   back to raw English text (keeps this callable from plain unit tests).
 * @return {{ action: "call" }|{ action: "busy", message: string }|{ action: "blocked" }}
 */
export function resolveCallTapAction(user, t) {
  if (isUserBusy(user)) {
    const name = resolveDisplayName(user, t);
    const message = t
      ? t("calls.errors.userBusyNamed", {
          defaultValue: "{{name}} is currently in another call.",
          name,
        })
      : `${name} is currently in another call.`;
    return { action: "busy", message };
  }

  if (!canCallUser(user)) {
    return { action: "blocked" };
  }

  return { action: "call" };
}
